import { ApolloClient, InMemoryCache, split } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { SetContextLink } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { isTokenExpired, refreshAuthToken, clearAuthStorage } from '../utils/auth';
import { GetCookie } from '../utils/cookie';
import { useConfigStore } from '../stores/ConfigStore';
import env from '../env';

const toWebSocketUrl = (httpUrl: string) => {
  if (httpUrl.startsWith('https://')) { return httpUrl.replace('https://', 'wss://'); }
  if (httpUrl.startsWith('http://')) { return httpUrl.replace('http://', 'ws://'); }
  return httpUrl;
};

export const createApolloClient = (uri: string = env.APP_GRAPHQL_ENDPOINT || '', enableSubscriptions = false) => {
  const httpLink = new HttpLink({
    uri,
  });

  const authLink = new SetContextLink(async (_: any, requestContext: any) => {
    const { authentication_type } = useConfigStore.getState().featureFlags?.common || {};
    const isKatonCognito = authentication_type === 'katon_cognito';

    let token = GetCookie('AccessToken');
    const refreshToken = GetCookie('RefreshToken');

    if (isKatonCognito && token && isTokenExpired(token) && refreshToken) {
      console.log('[Apollo] Token expired, attempting refresh...');

      let prevRole = '';
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/')));
        prevRole = JSON.parse(payload.custom_payload || '{}')?.role;
      } catch (error) {
        console.warn('[Apollo] Failed to parse previous role from JWT:', error);
      }

      const newToken = await refreshAuthToken(refreshToken);

      if (newToken) {
        console.log('[Apollo] Token refreshed successfully');
        token = newToken;

        try {
          const payload = JSON.parse(atob(newToken.split('.')[1].replaceAll('-', '+').replaceAll('_', '/')));
          const currentRole = JSON.parse(payload.custom_payload || '{}')?.role;
          if (currentRole && prevRole && currentRole !== prevRole) {
            console.log(`[Apollo] Role changed from ${prevRole} to ${currentRole}. Reloading...`);
            window.location.reload();
          }
        } catch (error) {
          console.warn('[Apollo] Failed to parse new role after refresh:', error);
        }
      } else {
        console.error('[Apollo] Refresh failed. Logging out...');
        clearAuthStorage();
        window.location.href = '/';
      }
    }

    const headers: Record<string, string> = {
      ...(requestContext.headers || {}),
    };

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    return { headers };
  });

  let link = authLink.concat(httpLink);

  if (enableSubscriptions && uri) {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: toWebSocketUrl(uri),
        connectionParams: () => {
          const token = GetCookie('AccessToken');
          return token ? { authorization: `Bearer ${token}` } : {};
        },
        retryAttempts: 10,
      }),
    );

    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      link,
    );
  }

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

let apolloClient: ApolloClient | null = null;
export const getApolloClient = () => {
  apolloClient ??= createApolloClient();
  return apolloClient;
};

let authApolloClient: ApolloClient | null = null;
export const getAuthApolloClient = () => {
  authApolloClient ??= createApolloClient(env.APP_API_GRAPHQL_ENDPOINT, true);
  return authApolloClient;
};
